import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, LogIn, Lock, Mail, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { publicAPI } from '../services/api';
import LandingNavbar from '../components/LandingNavbar';

const Login = () => {
  const { login, finalizeLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantBranding, setTenantBranding] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);

  // Force password-change state — populated when the API returns
  // `must_change_password: true`. We don't persist the session until the
  // user picks a new password, so refreshing mid-flow sends them back to
  // the login screen (correct behavior).
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempCreds, setTempCreds] = useState(null); // { token, user, tenant, subscription }
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        const slug = parts.length > 1 ? parts[0] : null;

        if (slug && slug !== 'www' && slug !== 'app') {
          const { data } = await publicAPI.getBranding(slug);
          if (data.success) setTenantBranding(data.data);
        }
      } catch (error) {
        console.log('No tenant branding found, using defaults');
      }
    };
    loadBranding();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('يرجى إدخال البريد الإلكتروني وكلمة المرور');
      setLoading(false);
      return;
    }

    const result = await login(email, password);

    if (!result.success) {
      setError(result.message);
    } else if (result.mustChangePassword) {
      // Staff user whose temp password was emailed — gate them on picking a
      // new one. Stash the temp credentials in component state so we can
      // finalize the session once /auth/change-password succeeds.
      setTempCreds({
        token: result.tempToken,
        user: result.tempUser,
        tenant: result.tempTenant,
        subscription: result.tempSubscription,
      });
      setMustChangePassword(true);
    } else if (result.redirectTo) {
      // Account isn't 'active' yet — route into the onboarding flow instead
      // of the dashboard. OnboardingFlow consults /onboarding/status on
      // mount and lands the user on the correct step (checkout / pending /
      // rejected).
      navigate(result.redirectTo, { replace: true });
    }

    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmNewPassword) {
      setError('يرجى تعبئة جميع الحقول');
      return;
    }
    if (newPassword.length < 8) {
      setError('كلمة المرور يجب أن تكون 8 أحرف على الأقل');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError('يجب أن تحتوي على حرف كبير وصغير ورقم');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('كلمتا المرور غير متطابقتين');
      return;
    }
    if (!tempCreds?.token) {
      setError('انتهت جلسة التغيير، رجاءً أعد تسجيل الدخول');
      setMustChangePassword(false);
      return;
    }

    setLoading(true);
    try {
      // Hit /auth/change-password directly with the ephemeral token from
      // login. Using the global `api` axios instance would attach whatever
      // token is in localStorage — we don't want that here.
      const axios = (await import('axios')).default;
      const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
      await axios.post(
        `${apiBase}/auth/change-password`,
        { currentPassword: password, newPassword },
        { headers: { Authorization: `Bearer ${tempCreds.token}` } },
      );

      // Server has flipped must_change_password=false; promote the temp
      // credentials to a real session.
      const { redirectTo } = await finalizeLogin(tempCreds);
      navigate(redirectTo || '/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'تعذّر تغيير كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  // ── Force password-change screen ─────────────────────────────
  // Shown after login when the server sets must_change_password=true. The
  // session is NOT committed until the user picks a new password here.
  if (mustChangePassword) {
    return (
      <>
        <LandingNavbar />
        <div
          className="min-h-screen flex items-center justify-center px-6 pt-[120px] pb-16 relative"
          dir="rtl"
          style={{ backgroundColor: '#FDFCF9' }}
        >
          <div
            className="absolute inset-0 opacity-[0.4] pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, #0F6E56 0.8px, transparent 0)',
              backgroundSize: '28px 28px',
              maskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
            }}
          />
          <div className="w-full relative z-10 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-[440px] bg-white rounded-3xl border border-[#E8E5DD] shadow-[0_10px_40px_-10px_rgba(15,110,86,0.12)] p-8 md:p-10"
            >
              <div className="flex justify-center mb-5">
                <img src="/listech-logo.svg" alt="LIS Techs" className="h-9 w-auto" />
              </div>

              <div className="text-center mb-6">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: '#FEF3C7' }}
                >
                  <Lock className="w-6 h-6" style={{ color: '#B45309' }} />
                </div>
                <h2
                  className="text-[1.4rem] font-bold mb-1"
                  style={{ color: '#1A1A1A', fontFamily: "'Readex Pro', sans-serif" }}
                >
                  تغيير كلمة المرور
                </h2>
                <p className="text-sm leading-[1.7]" style={{ color: '#8A8A8A' }}>
                  مرحباً {tempCreds?.user?.name || ''} — لحمايتك،
                  <br />
                  يجب تغيير كلمة المرور المؤقتة قبل الدخول
                </p>
              </div>

              {error && (
                <div
                  className="mb-5 p-3.5 rounded-xl flex items-center gap-3 text-sm border"
                  style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA', color: '#B91C1C' }}
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#444' }}>
                    كلمة المرور الجديدة
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full h-12 px-4 pr-11 rounded-xl text-sm border outline-none transition-all duration-200"
                      style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E5E5', color: '#1A1A1A' }}
                      onFocus={(e) => { e.target.style.borderColor = '#0F6E56'; e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.boxShadow = 'none'; }}
                      placeholder="8 أحرف على الأقل"
                      dir="ltr"
                    />
                    <Lock
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
                      style={{ color: '#BFBFBF' }}
                    />
                  </div>
                  <p className="text-[11.5px] mt-1.5 leading-[1.6]" style={{ color: '#8A8A8A' }}>
                    حرف كبير + حرف صغير + رقم
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#444' }}>
                    تأكيد كلمة المرور
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="w-full h-12 px-4 pr-11 rounded-xl text-sm border outline-none transition-all duration-200"
                      style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E5E5', color: '#1A1A1A' }}
                      onFocus={(e) => { e.target.style.borderColor = '#0F6E56'; e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.boxShadow = 'none'; }}
                      placeholder="••••••••"
                      dir="ltr"
                    />
                    <Lock
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
                      style={{ color: '#BFBFBF' }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl text-sm font-bold text-white transition-colors"
                  style={{
                    background: 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  {loading ? 'جارٍ الحفظ...' : 'تغيير كلمة المرور والدخول'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMustChangePassword(false);
                    setTempCreds(null);
                    setNewPassword('');
                    setConfirmNewPassword('');
                    setError('');
                  }}
                  className="w-full text-sm text-center mt-2"
                  style={{ color: '#8A8A8A' }}
                >
                  العودة لتسجيل الدخول
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LandingNavbar />
      {/* Single-panel login — the old split-screen decoration was visual
          noise that competed with the form for attention. Now: focused
          form, centred on a warm off-white canvas with a subtle dotted
          texture so the page doesn't feel empty. */}
      <div
        className="min-h-screen flex items-center justify-center px-6 pt-[120px] pb-16 relative"
        dir="rtl"
        style={{ backgroundColor: '#FDFCF9' }}
      >
        {/* Faint dotted texture to add depth without noise. */}
        <div
          className="absolute inset-0 opacity-[0.4] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #0F6E56 0.8px, transparent 0)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
          }}
        />

      {/* ── LOGIN CARD ── a single focused card, not a split-screen. */}
      <div className="w-full relative z-10 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[440px] bg-white rounded-3xl border border-[#E8E5DD] shadow-[0_10px_40px_-10px_rgba(15,110,86,0.12)] p-8 md:p-10"
        >

          {/* Logo — shown at top of card on every breakpoint (the old layout
              hid this behind `lg:hidden` because the decorative panel
              already carried the brand; that panel is gone now). */}
          <div className="flex justify-center mb-6">
            <img src="/listech-logo.svg" alt="LIS Techs" className="h-9 w-auto" />
          </div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="mb-7 text-center"
          >
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium mb-3"
              style={{ backgroundColor: '#0F6E560F', color: '#0F6E56' }}
            >
              <Lock className="w-3 h-3" />
              بوابة الموظفين
            </span>
            <h2
              className="text-[1.6rem] font-bold mb-1"
              style={{ color: '#1A1A1A', fontFamily: "'Readex Pro', sans-serif" }}
            >
              تسجيل الدخول
            </h2>
            <p className="text-sm" style={{ color: '#8A8A8A' }}>
              أهلاً بك، يرجى إدخال بياناتك للمتابعة
            </p>
          </motion.div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-5 p-3.5 rounded-xl flex items-center gap-3 text-sm border"
              style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA', color: '#B91C1C' }}
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          {/* Form */}
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#444' }}>
                البريد الإلكتروني
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 px-4 pr-11 rounded-xl text-sm border transition-all duration-200 outline-none"
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderColor: '#E5E5E5',
                    color: '#1A1A1A',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#0F6E56'; e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.boxShadow = 'none'; }}
                  placeholder="example@lab.com"
                  dir="ltr"
                />
                <Mail className="absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px]" style={{ color: '#BFBFBF' }} />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#444' }}>
                كلمة المرور
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 px-4 pr-11 pl-11 rounded-xl text-sm border transition-all duration-200 outline-none"
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderColor: '#E5E5E5',
                    color: '#1A1A1A',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#0F6E56'; e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.boxShadow = 'none'; }}
                  placeholder="••••••••"
                  dir="ltr"
                />
                <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px]" style={{ color: '#BFBFBF' }} />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: '#BFBFBF' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#666'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#BFBFBF'}
                >
                  {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                </button>
              </div>
            </div>

            {/* Remember / Forgot */}
            <div className="flex items-center justify-between pt-0.5">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                  style={{ accentColor: '#0F6E56' }}
                />
                <span className="text-sm" style={{ color: '#666' }}>تذكرني</span>
              </label>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm font-medium transition-colors"
                style={{ color: '#0F6E56' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0A5440'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#0F6E56'}
              >
                نسيت كلمة المرور؟
              </button>
            </div>

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.005 }}
              whileTap={{ scale: loading ? 1 : 0.995 }}
              className="w-full h-12 rounded-xl font-semibold text-white text-sm flex items-center justify-center gap-2 transition-all duration-200 mt-2"
              style={{
                background: loading ? '#1D9E75' : 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)',
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 14px rgba(15,110,86,0.25)',
              }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>جاري تسجيل الدخول...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  <span>تسجيل الدخول</span>
                </>
              )}
            </motion.button>
          </motion.form>

          {/* Other portals — staff login is one of three audiences (staff,
              patients, contractor labs). Surface both alternatives so users
              who landed on the wrong portal find their way quickly. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-6 pt-5 border-t border-[#EEE9DF] space-y-2"
          >
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full text-sm flex items-center justify-center gap-1.5 py-1.5 rounded-lg transition-colors"
              style={{ color: '#999' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#0F6E56'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#999'}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              هل أنت عميل؟ بوابة العملاء
            </button>
            <button
              type="button"
              onClick={() => navigate('/contractor/login')}
              className="w-full text-sm flex items-center justify-center gap-1.5 py-1.5 rounded-lg transition-colors"
              style={{ color: '#999' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#0F6E56'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#999'}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              هل أنت مختبر متعاقد؟ بوابة المختبرات
            </button>
          </motion.div>

          {/* Footer */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="mt-8 text-center text-xs"
            style={{ color: '#C0C0C0' }}
          >
            &copy; {new Date().getFullYear()} {tenantBranding?.name || 'LIS Techs'}. جميع الحقوق محفوظة
          </motion.p>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default Login;
